import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { camelize } from '@ember/string';
import { t } from 'ember-intl';
import { eq, and, gte, or, gt, lte } from 'ember-truth-helpers';
import { concat, fn, get } from '@ember/helper';
import { on } from '@ember/modifier';
import {
  HdsCodeBlock,
  HdsAlert,
  HdsFormTextInputField,
  HdsButton,
  HdsIcon,
  HdsSeparator,
  HdsFormHelperText,
} from '@hashicorp/design-system-components/components';
import { Typography, Disclosure, FlexGrid, Flex } from 'core/components';
import { classNames } from 'core/helpers';
import { boxMargin, boxPadding } from 'core/modifiers';

/**
 *
 * `DomainVerification` is the first step of both the OIDC and SAML SSO configuration flows.
 * Users are prompted to copy a TXT record to verify within their DNS, then to add
 * their email address domain(s).
 *
 *
 * ```
 * <DomainVerification />
 * ```
 *
 * @class DomainVerification
 *
 */
export default class DomainVerification extends Component {
  @tracked steps = [
    'components.page.organizations.sso.configure.common.step-1.instructions.1',
    'components.page.organizations.sso.configure.common.step-1.instructions.2',
    'components.page.organizations.sso.configure.common.step-1.instructions.3',
    'components.page.organizations.sso.configure.common.step-1.instructions.4',
    'components.page.organizations.sso.configure.common.step-1.instructions.5',
  ];
  <template>
    <div data-test-domain-verification>
      <HdsFormHelperText {{boxPadding "0 0 2xs 0"}} data-test-subtitle>
        {{t
          "components.page.organizations.sso.configure.common.step-1.subtitle"
        }}
      </HdsFormHelperText>
      <Typography
        @variant="h2"
        class="hds-typography-display-300 hds-font-weight-semibold"
        data-test-title
        {{boxPadding "0 0 2xs 0"}}
      >
        {{t "components.page.organizations.sso.configure.common.step-1.title"}}
      </Typography>
      <Typography
        @variant="body1"
        class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
        data-test-helper
        {{boxPadding "0 0 sm 0"}}
      >
        {{t "components.page.organizations.sso.configure.common.step-1.helper"}}
      </Typography>
      <div {{boxPadding "xs 0 0 0"}}>
        <HdsCodeBlock
          @value={{@domainTxtVerificationRecord}}
          @hasCopyButton={{true}}
          @hasLineNumbers={{false}}
          @hasLineWrapping={{true}}
          data-test-txt
        />
        <Disclosure
          data-test-disclosure
          {{boxPadding "md 0"}}
          @label={{t
            "components.page.organizations.sso.configure.common.step-1.instructions.show-me"
          }}
          @openLabel={{t
            "components.page.organizations.sso.configure.common.step-1.instructions.show-me"
          }}
        >
          <HdsAlert
            @type="compact"
            data-test-instructions
            {{boxMargin "sm 0"}}
            as |A|
          >
            <A.Description>
              {{t
                "components.page.organizations.sso.configure.common.step-1.instructions.info"
              }}
            </A.Description>
          </HdsAlert>
          <Typography>
            <ol>
              {{#each this.steps as |step|}}
                <li
                  class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
                >
                  {{t step htmlSafe=true}}
                </li>
              {{/each}}
            </ol>
          </Typography>
        </Disclosure>
        <div data-test-domain-form-field {{boxPadding "sm 0 0 0"}}>
          <FlexGrid as |G|>
            <G.Item
              class="domain-verification-container"
              @xs="12"
              @sm="12"
              @md="12"
              @lg="12"
            >
              {{#each @domains key="index" as |domain index|}}
                <div>
                  <div class="domain-input">
                    <G.Item @xs="6" @sm="8" @md="8" @lg="6">
                      <@errorComponent
                        data-test-domain-field-error
                        @field={{if
                          (eq @ssoConfigType "Saml")
                          (if
                            domain
                            (concat "config.Details.Saml.email_domains." index)
                            "config.Details.Saml.email_domains"
                          )
                          "email_domain"
                        }}
                        @hdsDelegatedError={{true}}
                        as |err|
                      >
                        <HdsFormTextInputField
                          @value={{domain}}
                          @isInvalid={{err}}
                          @isRequired={{if (eq index 0) true}}
                          @type="text"
                          data-test-domain-input={{index}}
                          placeholder={{t
                            "components.page.organizations.sso.configure.common.step-1.domain-verification.input.placeholder"
                          }}
                          {{on "input" (fn @updateDomain index)}}
                          as |F|
                        >
                          <F.Label>
                            {{#if (eq index 0)}}
                              {{t
                                "components.page.organizations.sso.configure.common.step-1.domain-verification.label"
                                numDomains=@domains.length
                              }}
                            {{/if}}
                          </F.Label>
                          {{#if (and (gte @unverifiedDomains.length 0) domain)}}
                            {{#let
                              (get
                                @unverifiedReasonsGroupedByDomain
                                (camelize domain)
                              )
                              as |reason|
                            }}
                              {{#if reason}}
                                <F.Error data-test-domain-failure-reason as |E|>
                                  <E.Message>
                                    {{t
                                      "components.page.organizations.sso.configure.common.step-1.domain-verification.error"
                                      reason=reason
                                      domain=domain
                                    }}
                                  </E.Message>
                                </F.Error>
                              {{/if}}
                            {{/let}}
                          {{/if}}
                          {{#if err}}
                            <F.Error>
                              {{err.description}}
                            </F.Error>
                          {{/if}}
                        </HdsFormTextInputField>
                      </@errorComponent>
                    </G.Item>
                    <div class="verify-delete-button">
                      {{#if (eq index @lastDomainIndex)}}
                        <HdsButton
                          data-test-verify-domains
                          @color="tertiary"
                          @icon="check-circle"
                          @text={{t
                            "components.page.organizations.sso.configure.common.step-1.domain-verification.title"
                            numDomains=@domains.length
                          }}
                          disabled={{or
                            @verifyDomains.isRunning
                            (eq @emailDomains.length 0)
                          }}
                          {{on "click" @verifyDomains.perform}}
                          {{boxMargin "0 0 0 sm"}}
                        />
                      {{else}}
                        <HdsButton
                          data-test-domain-remove={{index}}
                          @color="tertiary"
                          @icon="trash"
                          @text={{t
                            "components.page.organizations.sso.configure.common.step-1.domain-verification.remove"
                          }}
                          {{on "click" (fn @removeDomain index)}}
                          {{boxMargin "0 0 0 sm"}}
                        />
                      {{/if}}
                    </div>
                  </div>
                  <FlexGrid as |G|>
                    <G.Item @xs="10" @sm="10" @md="10" @lg="10">
                      {{#if (and (gt @verifiedDomains.length 0) domain)}}
                        {{#let
                          (get
                            @verifiedReasonsGroupedByDomain (camelize domain)
                          )
                          as |reason|
                        }}
                          {{#if
                            (or
                              (and reason (eq @ssoConfigType "Saml"))
                              (eq @ssoConfigType "Oidc")
                            )
                          }}
                            <Flex
                              data-test-domain-success-message
                              @alignItems="center"
                              @gap="2xs"
                              {{boxPadding "2xs 0 sm 0"}}
                            >
                              <HdsIcon
                                class="domain-input__success-icon"
                                @name="check-circle-fill"
                                @color="var(--token-color-palette-green-200)"
                                @isInline={{true}}
                              />
                              <span
                                class={{classNames
                                  "domain-input__success-text"
                                  "hds-typography-body-200"
                                  "hds-font-weight-medium"
                                  "hds-foreground-faint"
                                }}
                              >
                                {{t
                                  "components.page.organizations.sso.configure.common.step-1.domain-verification.success"
                                }}
                              </span>
                            </Flex>
                          {{/if}}
                        {{/let}}
                      {{/if}}
                    </G.Item>
                  </FlexGrid>
                  {{#if
                    (and (eq @ssoConfigType "Saml") (eq index @lastDomainIndex))
                  }}
                    <HdsButton
                      class="add-domain-button"
                      data-test-domain-add
                      @color="tertiary"
                      @icon="plus"
                      @text={{t
                        "components.page.organizations.sso.configure.common.step-1.domain-verification.add"
                      }}
                      disabled={{or
                        @verifyDomains.isRunning
                        (lte @verifiedDomains.length index)
                      }}
                      {{on "click" (fn @addDomain index)}}
                    />
                  {{/if}}
                </div>
              {{/each}}
            </G.Item>
          </FlexGrid>
        </div>
      </div>
    </div>
    <div {{boxPadding "0 0 sm 0"}}>
      <HdsSeparator />
    </div>
  </template>
}
